import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { isBilling2023V2Subscription } from '@mm/state/model/SubscriptionModel';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { enhancedPlansSelector } from '@mm/state/selectors/subscriptionSelectors';
import { useSelector } from 'react-redux';
import {
  activeFacebookPageSelector,
  getPageWithBillingInfo,
} from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { SettingsRoutes } from '../../../../../routes/settings-routes';
import { getBasePath } from '../SubscriptionTabsArea/ActiveSubscriptionTab/helpers';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { notification } from 'antd';
import {
  activeCompanySelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import React, { ReactNode, useEffect, useState } from 'react';
import imageSrc from 'assets/cancel-subscription.png';
import agencyTabIcon from './icons/agency.png';
import metaTabIcon from './icons/meta-icon.png';
import metaEmailTabIcon from './icons/promotion.png';
import emailTabIcon from './icons/cart.png';
import {
  PlanBillingPeriodBE,
  PlanSubtypes,
  planSubtypesByType,
} from '@mm/state/interfaces/subscription';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { BookDemoButton } from '../../../../../common/buttons/BookDemoButton/BookDemoButton';
import styles from './BillingPlans.module.css';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { BOOK_DEMO_NON_ENTERPRISE } from '../../../XRayOnboarding/WebsiteReportSummary/constants';
import classNames from 'classnames';
import leadMagnetIcon from 'assets/icons/lead-magnet-icon.svg';
import likeIcon from 'assets/icons/like-outlined.svg';
import userIcon from 'assets/icons/user.svg';

export type BillingPlanCardProps = {
  title: string;
  header?: null | string;
  price: string | ReactNode;
  pricePeriod?: string;
  priceNote?: string;
  buttonName?: string;
  onClick?: () => void;
  buttonComponent?: (disabled?: boolean) => ReactNode;
  planSubtypes?: PlanSubtypes[];
  points?: string[];
  pointsIcons?: string[];
  pointsTitle?: string;
  secondaryPoints?: (string | ReactNode)[];
  costs?: string[] | ReactNode;
  tag?: string;
  cardBgColor?: string;
  tagBgColor?: string;
  hide?: boolean;
  isCustom?: boolean;
  isFreeTrial?: boolean;
  category?: BillingTabCategory;
  currentTag?: boolean;
  planCode?: string;
};

export type BillingTabCategory =
  | 'meta'
  | 'email'
  | 'metaEmail'
  | 'agency'
  | 'other';

export type BillingPlanTab = {
  category: BillingTabCategory;
  name: string;
  icon: string;
  mission?: string;
};

export type ManageSubsctiptionRouteParams = {
  tabId: string;
  pageId: string;
};

// tslint:disable-next-line:cyclomatic-complexity
export const useBillingPlans = ({
  isAnnual,
  onBillingSuccess,
  hideFreePlan,
  landmineOpenId,
}) => {
  const plans = useSelector(enhancedPlansSelector);
  const basicFacebookPages = useSelector(
    (state: StoreState) => state.connectedPage.basicPagesList,
  );
  const plansMapping = useSelector(
    (state: StoreState) => state.subscription.plansMapping,
  );
  const company = useSelector(activeCompanySelector);
  const isAgency = company?.companyAttributes?.agency;
  const companyId = useSelector(selectActiveCompanyId);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const { tabId, pageId } = useParams<ManageSubsctiptionRouteParams>();
  const location = useLocation();
  const history = useHistory();
  const activeFacebookPage = pageId
    ? basicFacebookPages.find(p => p.id === Number(pageId))
    : facebookPage;

  const currentPlan = plans.find(plan =>
    tabId
      ? plan.planCode === tabId
      : activeFacebookPage?.subscriptions?.find(
          sub => sub.planCode === plan.planCode,
        ),
  );

  const isFreePlan = !activeFacebookPage?.subscriptions?.length;
  const isCurrentTeamCustomPlan = !!currentPlan?.planSubtypes.find(
    subtype => subtype === PlanSubtypes.teamCustom,
  );
  const isManageSubscriptionsPage = location.pathname.includes(
    getBasePath(SettingsRoutes.billing.manageSubscrptions.path),
  );

  const canCancelSubscription =
    !isFreePlan &&
    isManageSubscriptionsPage &&
    !(
      activeFacebookPage.subscription?.state === 'canceled' ||
      activeFacebookPage.downgradePending
    ) &&
    !activeFacebookPage.redemption?.freeTrial;

  const plansToDowngrade = plans.filter(plan =>
    currentPlan?.downgradePlansIds.includes(plan.id),
  );

  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal();

  const { closeModal, openBillingModal, openNewInfoModal } = useModalActions();

  const { retrieveBasicListByCompany } = useConnectedPageCRUDActions();
  const subscriptionCRUDActions = useSubscriptionCRUDActions();
  const { update } = useCompanyCRUDActions();

  const [isAwaitingCancellation, setIsAwaitingCancellation] = useState(false);

  useEffect(() => {
    const getSubscriptionCancellationStatus = async () => {
      if (activeFacebookPage?.subscription?.id) {
        const hasCancels =
          await subscriptionCRUDActions.getCurrentSubscriptionCancels(
            activeFacebookPage.subscription.id,
          );

        if (hasCancels) {
          setIsAwaitingCancellation(true);
        }
      }
    };

    if (activeFacebookPage?.subscription?.id) {
      getSubscriptionCancellationStatus();
    }
  }, [activeFacebookPage?.subscription?.id]);

  const handleBack = () => {
    history.push(SettingsRoutes.billing.getLink());
  };

  const onRequestEnteriprisePlanScheduled = async () => {
    await update({
      id: companyId,
      bookAnEnterprise: true,
    });
    if (isManageSubscriptionsPage) {
      handleBack();
    } else {
      closeModal();
    }
  };

  const bookDemoButton = (disabled: boolean) => (
    <BookDemoButton
      button={disabled ? 'Current Plan' : 'Schedule a Demo'}
      className={classNames(styles.button, {
        [styles.disabledButton]: disabled,
      })}
      calendlyUrl={BOOK_DEMO_NON_ENTERPRISE}
      utm={{
        utmSource: 'inproduct',
        utmMedium: 'billing',
        utmCampaign: 'requestEnterprisePlan',
      }}
      onEventScheduled={onRequestEnteriprisePlanScheduled}
      show
    />
  );

  const getCurrentPlan = (
    planSubtypes: PlanSubtypes[],
    billingPeriod: PlanBillingPeriodBE,
  ) => {
    const currentPlan = plans.find(
      p =>
        planSubtypes.every(subtype => p.planSubtypes.includes(subtype)) &&
        p.billingPeriod === billingPeriod,
    );
    return { currentPlan };
  };

  const getPlanPrice = (
    planSubtypes: PlanSubtypes[],
    billingPeriod: PlanBillingPeriodBE,
  ) => {
    const { currentPlan } = getCurrentPlan(planSubtypes, billingPeriod);
    return (currentPlan?.unitAmountInCents || 0) / 100;
  };

  const hideCheaperPlans = (
    planSubtypes: PlanSubtypes[],
    billingPeriod: PlanBillingPeriodBE,
  ) => {
    const { currentPlan: optionPlan } = getCurrentPlan(
      planSubtypes,
      billingPeriod,
    );

    if (currentPlan && isCurrentTeamCustomPlan && optionPlan) {
      return currentPlan.unitAmountInCents > optionPlan?.unitAmountInCents;
    }

    return false;
  };

  const upgradeToPlan = async (
    planSubtypes: PlanSubtypes[],
    billingPeriod: PlanBillingPeriodBE,
  ) => {
    let enhancedBillingPage;

    if (!activeFacebookPage && !basicFacebookPages.length) {
      const res = await retrieveBasicListByCompany(companyId);
      enhancedBillingPage = getPageWithBillingInfo(
        res?.[0],
        plans,
        plansMapping,
      );
    } else {
      enhancedBillingPage = activeFacebookPage;
    }

    const subscriptionId = enhancedBillingPage?.subscription
      ? enhancedBillingPage.subscription.id
      : undefined;

    const { currentPlan } = getCurrentPlan(planSubtypes, billingPeriod);
    const downgrade = plansToDowngrade.find(
      plan => currentPlan?.planCode === plan.planCode,
    );

    if (
      !isBilling2023V2Subscription(enhancedBillingPage?.subscription) &&
      !isFreePlan &&
      !isAgency
    ) {
      openNewInfoModal({
        title: 'Contact Support',
        description: (
          <div>
            Please contact support at{''}{' '}
            <a
              className="pause-subscription-modal-link"
              href="mailto:cs@customers.ai"
            >
              cs@customers.ai
            </a>
            <br />
            to upgrade your subscription
          </div>
        ),
        image: imageSrc,
        button: 'Ok',
        onClick: () => closeModal(),
      });
      return;
    }

    if (currentPlan && enhancedBillingPage) {
      await openBillingModal({
        facebookPage: enhancedBillingPage,
        plan: currentPlan,
        landmineOpenId,
        manualRedirect: true,
        subscription: enhancedBillingPage?.subscription,
        upgradeFromSubscriptionId: !downgrade ? subscriptionId : undefined,
        downgradeFromSubscriptionId: downgrade ? subscriptionId : undefined,
        onSuccessBilling: onBillingSuccess,
        onPlanCreatedFailed: () =>
          notification.error({
            message: 'Something went wrong',
            description:
              'Subscription is not created. Credit card is not charged.',
          }),
        isMonthly: true,
        hideAnnualToggle: true, // annual plans are not implemented properly on BE, for now hide them
      });
    }
  };

  const planPeriod = isAnnual
    ? PlanBillingPeriodBE.year
    : PlanBillingPeriodBE.month;

  const billingPlanTabs: BillingPlanTab[] = [
    {
      category: 'other',
      name: 'Current Plan',
      icon: agencyTabIcon,
    },
    {
      category: 'meta',
      name: 'Meta Ads',
      icon: metaTabIcon,
      mission:
        'Fix broken Meta Ads data to eliminate wasted spend & increase conversions',
    },
    {
      category: 'email',
      name: 'Email for Ecommerce or B2B',
      icon: emailTabIcon,
      mission: 'Identify 20% of website visitors & supercharge email marketing',
    },
    {
      category: 'metaEmail',
      name: 'Ads + Email',
      icon: metaEmailTabIcon,
      mission: 'Improve ad performance and supercharge email marketing',
    },
    {
      category: 'agency',
      name: 'Marketing Agencies',
      icon: agencyTabIcon,
      mission:
        'Superpower your client results with website visitor ID technology',
    },
  ];

  const billingPlans: BillingPlanCardProps[] = [
    {
      title: currentPlan?.shortName || '',
      price: `$${(currentPlan?.unitAmountInCents || 0) / 100}`,
      pricePeriod: currentPlan?.billingPeriod === 'month' ? '/mo' : 'year',
      buttonName: 'Current Plan',
      planSubtypes: currentPlan?.planSubtypes,
      hide: !isManageSubscriptionsPage || !currentPlan || isAgency,
      category: 'other',
    },
    {
      title: 'Free',
      price: '$0',
      pricePeriod: '',
      buttonName: 'Sign Up Free',
      onClick: () => {
        onBillingSuccess?.();
        closeModal();
      },
      points: ['50 Leads Included', '150 Email Sends Included'],
      costs: ['up to 5,000 leads'],
      hide: hideFreePlan,
      category: 'other',
    },
    {
      title: 'Free Trial',
      header: null,
      price: '',
      buttonName: 'Current Plan',
      onClick: () => {
        onBillingSuccess?.();
        closeModal();
      },
      secondaryPoints: !isAgency
        ? ['Capture, contact, and convert contacts!']
        : [],
      tag: 'Up to 500 leads FREE',
      tagBgColor: '#389E0D',
      hide: !isFreeTrialPromotionActive || !isManageSubscriptionsPage,
      isFreeTrial: true,
      category: 'other',
    },
    {
      title: 'For Meta Advertisers',
      price: `$${getPlanPrice(planSubtypesByType.restore, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/mo',
      priceNote: '+ applicable CPM fees ',
      buttonName: !isFreePlan ? 'Upgrade' : 'Get Started',
      planSubtypes: planSubtypesByType.restore,
      onClick: () => upgradeToPlan(planSubtypesByType.restore, planPeriod),
      buttonComponent: bookDemoButton,
      secondaryPoints: [
        'Fully enrich 1st-party website data, encrypt and send back to your Ads Manager via Meta CAPI or Meta Custom Audiences',
        'Fully restore signal loss due to third-party cookie deprecation resulting in increases in Event Match Quality Score to +7/10',
        '+30% ROAS',
      ],
      costs: [
        'Up to 5,000 contacts',
        '$10 CPM',
        '5k-100k contacts',
        '$9 CPM',
        '+100k - Unlimited / month',
        'Call for custom pricing for websites with high volume traffic or ad spend',
      ],
      hide:
        hideCheaperPlans(planSubtypesByType.restore, planPeriod) || isAgency,
      category: 'meta',
    },
    {
      title: 'For Email Marketers',
      price: `$${getPlanPrice(planSubtypesByType.starterV2, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/mo',
      priceNote: '+ applicable contact capture fees',
      buttonName: !isFreePlan ? 'Upgrade' : 'Get Started',
      planSubtypes: planSubtypesByType.starterV2,
      onClick: () => upgradeToPlan(planSubtypesByType.starterV2, planPeriod),
      buttonComponent: bookDemoButton,
      secondaryPoints: [
        'Website Visitor ID Pixel',
        'Integrations with your ESP (Klaviyo, Sendlane, Salesforce, MailChimp, HighLevel, SendGrid, Gmail, Zapier, etc.) ',
        'Consumer and B2B Contact Enrichment (Phone, home/business address for postcards, etc.)',
        'Email Deliverability Testing',
        'AI copywriting',
      ],
      costs: ['$0.23 / contact', '$9 CPM'],
      hide:
        hideCheaperPlans(planSubtypesByType.starterV2, planPeriod) || isAgency,
      category: 'email',
    },
    {
      title: 'For Meta Advertisers + Email Marketers',
      price: `$${getPlanPrice(planSubtypesByType.proV2, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/mo',
      priceNote: '+ applicable CPM and contact capture fees ',
      buttonName: !isFreePlan ? 'Upgrade' : 'Get Started',
      planSubtypes: planSubtypesByType.proV2,
      onClick: () => upgradeToPlan(planSubtypesByType.proV2, planPeriod),
      buttonComponent: bookDemoButton,
      secondaryPoints: [
        'Fully enrich 1st-party website data, encrypt and send back to your Ads Manager via Meta CAPI or Meta Custom Audiences',
        'Fully restore signal loss due to third-party cookie deprecation resulting in increases in Event Match Quality Score to +7/10',
        '+30% ROAS',
        'Website Visitor ID Pixel',
        'Integrations with your ESP (Klaviyo, Sendlane, Salesforce, MailChimp, HighLevel, SendGrid, Gmail, Zapier, etc.) ',
        'Consumer and B2B Contact Enrichment (Phone, home/business address for postcards, etc.)',
        'Email Deliverability Testing',
        'AI copywriting',
      ],
      costs: [
        'Up to 5,000 contacts',
        '$10 CPM',
        '5k-100k contacts',
        '$9 CPM',
        '+100k - Unlimited / month',
        'Call for custom pricing for websites with high volume traffic or ad spend',
        '$0.18 / contact captured',
      ],
      tag: 'Most popular',
      tagBgColor: '#63A4C9',
      hide: hideCheaperPlans(planSubtypesByType.proV2, planPeriod) || isAgency,
      category: 'metaEmail',
    },
    {
      title: 'Agency Starter',
      price: `$${getPlanPrice(planSubtypesByType.agencyLevel1, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/month',
      buttonName: 'Upgrade to Plan',
      planSubtypes: planSubtypesByType.agencyLevel1,
      onClick: () => upgradeToPlan(planSubtypesByType.agencyLevel1, planPeriod),
      buttonComponent: undefined,
      secondaryPoints: [
        '500 X-Ray Leads Included monthly',
        '3 sub-client accounts',
      ],
      pointsIcons: [leadMagnetIcon, userIcon],
      costs: ['$0.20 per X-Ray Lead'],
      hide: !isAgency,
      category: 'agency',
    },

    {
      title: 'Agency Growth',
      price: `$${getPlanPrice(planSubtypesByType.agencyLevel2, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/month',
      buttonName: 'Upgrade to Plan',
      planSubtypes: planSubtypesByType.agencyLevel2,
      onClick: () => upgradeToPlan(planSubtypesByType.agencyLevel2, planPeriod),
      buttonComponent: undefined,
      secondaryPoints: [
        '1,500 X-Ray Leads Included monthly',
        '10 sub-client accounts',
      ],
      pointsIcons: [leadMagnetIcon, userIcon],
      costs: ['$0.18 per X-Ray Lead'],
      hide: !isAgency,
      category: 'agency',
    },
    {
      title: 'Agency Unlimited',
      price: `$${getPlanPrice(planSubtypesByType.agencyLevel3, planPeriod)}`,
      pricePeriod: isAnnual ? '/year' : '/month',
      buttonName: 'Upgrade to Plan',
      planSubtypes: planSubtypesByType.agencyLevel3,
      onClick: () => upgradeToPlan(planSubtypesByType.agencyLevel3, planPeriod),
      buttonComponent: undefined,
      secondaryPoints: [
        '3,500 X-Ray Leads Included monthly',
        '50 sub-client accounts',
        'Dedicated Success Rep',
      ],
      pointsIcons: [leadMagnetIcon, userIcon, likeIcon],
      costs: ['$0.16 per X-Ray Lead'],
      hide: !isAgency,
      category: 'agency',
    },
  ];

  return {
    billingPlanTabs,
    billingPlans,
    activeFacebookPage,
    currentPlan,
    tabId,
    pageId,
    plansToDowngrade,
    canCancelSubscription,
    isAwaitingCancellation,
    isManageSubscriptionsPage,
    getCurrentPlan,
  };
};
